import * as React from "react";
import {useEffect, useState} from "react";
import {Box, Drawer, IconButton, Link, Typography, useMediaQuery, useTheme} from "@mui/material";
import { positions } from '@mui/system';
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CachedIcon from '@mui/icons-material/Cached';
import ChatbotOpenImg from "../../images/ChatbotOpen.png";
import {useResources} from "../../stores/ResourceProvider";
import {useNavigate} from "react-router-dom";
import RoutesConfig from "../../RoutesConfig";
import {UserType} from "../../API";

enum ChatDrawerView {
  AskFinn = "AskFinn",
  FinnTips= "FinnTips"
}

const ChatbotDrawer = ({
  open,
  view,
  onChange
}: {
  open?: boolean
  view?: string
  onChange(open: boolean, view: string): void
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [width, setWidth] = useState<number>(0)
  const [currentView, setCurrentView] = useState<string>(ChatDrawerView.AskFinn)
  const [isFree, setIsFree] = useState<boolean>(false)

  const openWidth = 400
  const {userStore} = useResources()
  const navigate = useNavigate()

  useEffect(() => {
    setIsOpen(open === true)
    setIsFree(userStore.user !== undefined && userStore.user.userType === UserType.Free)
    if (open === true) {
      setWidth(openWidth)
      if (view) {
        setCurrentView(view)
      }
    } else {
      setWidth(0)
    }
  }, [open, view])

  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('xl'))
  const styles = {
    panel: {
      display: "flex",
      width: width,
      flexShrink: 0,
      padding:0
    },
    overlay: {
      position: "absolute",
      top: 80,
      right: 0,
      height: 'calc(100vh - 80px)',
      width: width,
      minWidth: width,
      padding:0,
      zIndex: 9999,
    },
    drawer: {
      width: openWidth,
      flexShrink: 0
    },
    chatCloseBtn: {
      color:"inherit",
      p:0,
      mr:"16px",
      width: 24,
      height: 24,
      backgroundColor: `#dfdfee`,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
      },
    },
    tipHeading: {
      fontSize: 15,
      fontWeight: 700
    },
    tipText: {
      fontSize: 15,
      lineHeight: 1.4
    },
    tipLink: {
      fontSize: 15,
      textDecoration: "none"
    },
    viewActive: {
      display:"inline-flex",
      borderBottom:"2px solid green",
      whiteSpace:"nowrap"
    },
    viewInactive: {
      display:"inline-flex",
      whiteSpace:"nowrap"
    },
    viewLinkActive: {
      fontSize: 14,
      textDecoration: "none",
      color: theme.palette.primary.main
    },
    viewLinkInactive: {
      fontSize: 14,
      textDecoration: "none",
      color: theme.palette.text.primary
    }
  }

  const handleDrawerOpen = () => {
    onChange(true, currentView)
  };

  const handleDrawerClose = () => {
    onChange(false, currentView);
  };

  const toggleDrawer = () => {
    onChange(!isOpen, currentView)
  }

  const toggleView = () => {
    if (currentView === ChatDrawerView.FinnTips) {
      setCurrentView(ChatDrawerView.AskFinn)
      onChange(isOpen, ChatDrawerView.AskFinn)
    } else {
      setCurrentView(ChatDrawerView.FinnTips)
      onChange(isOpen, ChatDrawerView.FinnTips)
    }
  }

  const changeView = (view: ChatDrawerView) => {
    setCurrentView(view)
    onChange(isOpen, view)
  }

  const renderNewYearTip = () => {
    return (
      <Box sx={{display:"flex", padding:2, pt:"12px", justifyContent:"center", alignItems:"bottom"}}>
        <Box sx={{display:"flex", flexDirection:"column", flexGrow:0, flexShrink:0, justifyContent:"flex-end", width:30, height:"100%", padding:0}}>
          <img src={ChatbotOpenImg} alt="chatbot" height={30} width={30} style={{borderRadius:"15px"}}/>
        </Box>
        <Box sx={{display:"flex", flexDirection:"column", flexGrow:1, ml:1,  bgcolor:"#f1f1f2", borderTopLeftRadius:20, borderTopRightRadius:20, borderBottomRightRadius:20, padding:2}}>
          <Typography variant="body2" sx={styles.tipText}>
            <b>Happy New Year!</b>
          </Typography>
          <Typography variant="body2" sx={styles.tipText}>
            Here are tips to get off to a fresh start in RBC for 2025:
          </Typography>
          <Typography variant={"body2"} sx={styles.tipHeading}>
            <Link href="#" onClick={() => navigate(`${RoutesConfig.calc.pathname}/timeline`)} sx={styles.tipLink}>
              Timeline<ChevronRightIcon sx={{height:22, position:"relative", top:"6px", left:"-4px"}}/>
            </Link>
          </Typography>
          <ol style={{paddingLeft:"16px", marginBottom:0}}>
            <li style={styles.tipText}>
              Update any changes to your marital status and retirement date(s).
            </li>
          </ol>
          <Typography variant={"body2"} sx={styles.tipHeading}>
            <Link href="#" onClick={() => navigate(`${RoutesConfig.calc.pathname}/networth`)} sx={styles.tipLink}>
              Assets & Liabilities<ChevronRightIcon sx={{height:22, position:"relative", top:"6px", left:"-4px"}}/>
            </Link>
          </Typography>
          <ol style={{paddingLeft:"16px", marginBottom:0}} start={2}>
            <li style={styles.tipText}>
              <b>Assets:</b> Update the Balance, 2024 End Balance, YTD Withdrawal and any others.
            </li>
            <li style={styles.tipText}>
              <b>Asset Conversions:</b> Mark any 2024 conversions as Complete.
            </li>
            <li style={styles.tipText}>
              <b>Liabilities:</b> Update the Balance field and any others.
            </li>
            <li style={styles.tipText}>
              <b>Archive</b> any closed Asset and Liability accounts.
            </li>
            <li style={styles.tipText}>
              <b>Add</b> any new Assets or Liabilities.
            </li>
            <li style={styles.tipText}>
              <b>Snapshot:</b> Create a new Asset & Liability Snapshot.
            </li>
          </ol>
          <Typography variant={"body2"} sx={styles.tipHeading}>
            <Link href="#" onClick={() => navigate(`${RoutesConfig.calc.pathname}/budget`)} sx={styles.tipLink}>
              Budget<ChevronRightIcon sx={{height:22, position:"relative", top:"6px", left:"-4px"}}/>
            </Link>
          </Typography>
          <ol style={{paddingLeft:"16px", marginBottom:0}} start={8}>
            <li style={styles.tipText}>
              <b>Incomes:</b> Update the Amount and any other changes.
            </li>
            <li style={styles.tipText}>
              <b>Expenses:</b> Update the Amount and any other changes.
            </li>
            <li style={styles.tipText}>
              <b>Archive</b> any inactive Incomes or Expenses.
            </li>
            <li style={styles.tipText}>
              <b>Add</b> any new Incomes or Expenses.
            </li>
            <li style={styles.tipText}>
              <b>Snapshot:</b> Create a new Budget Snapshot on the Budget History view.
            </li>
          </ol>
        </Box>
      </Box>
    )
  }

  const renderTips = () => {
    return (
      <Box sx={{display:"flex", minHeight: 700, width: openWidth, flexDirection:"column", justifyContent:"stretch"}}>
        {/*<Box sx={{display:"flex", height:76, bgcolor:"#fcfcfc", border:"1px solid #eee", width:"100%", paddingLeft:"20px", flexGrow:0, justifyContent:"flex-start", alignItems:"center"}}>*/}
        <Box sx={{position:"fixed", display:"flex", height:72, bgcolor:"#fcfcfc", boxShadow:"rgba(0, 0, 0, 0.05) 0px 1px 1px 0px", width:openWidth, paddingLeft:"20px", justifyContent:"flex-start", alignItems:"center"}}>
          <img src={ChatbotOpenImg} alt="chatbot" height={40} style={{borderRadius:"20px"}}/>
          <Typography variant="h6" sx={{pl:1, fontSize:15, whiteSpace:"nowrap"}}>Finn Tips</Typography>
          <Box sx={{display:"flex", flexGrow:1, justifyContent:"flex-end", pr:"12px"}}>
            <IconButton sx={{color:theme.palette.text.primary}}
                        onClick={() => setCurrentView(ChatDrawerView.AskFinn)}>
              <CachedIcon/>
            </IconButton>
          </Box>
        </Box>
        <Box sx={{display:"flex", flexDirection:"column", flexGrow:1, overflowY:"scroll", marginTop:"80px"}}>
          {renderNewYearTip()}
          <Box sx={{display:"flex", bgcolor:"#f1f1f2", borderRadius:"5px", border:"1px solid #e4e4e8", margin:2, px:1, pt:0, pb:"2px"}}>
            <Link href="#" onClick={() => setCurrentView(ChatDrawerView.AskFinn)} sx={styles.tipLink}>
              Ask Finn another question<ChevronRightIcon sx={{height:22, position:"relative", top:"6px", left:"-4px"}}/>
            </Link>
          </Box>
        </Box>
      </Box>
    )
  }

  if (!isOpen) {
    return null
  }

  return (
    <Drawer
      variant={isLarge ? "permanent" : "permanent"}
      anchor="right"
      open={isOpen}
      sx={styles.drawer}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        // necessary for content to be below app bar
        // ...theme.mixins.toolbar,
        justifyContent: 'flex-start',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'rgba(0, 0, 0, 0.25) 0px 1px 3px 0px',
        marginTop: "76px"
      }}>
        <Box sx={{position:"fixed", top:126, right:4, zIndex:999}}>
          <Box sx={{display:"inline-flex", pr:"160px", height:"24px"}}>
            <Box sx={currentView === ChatDrawerView.AskFinn ? styles.viewActive : styles.viewInactive} mr={2}>
              <Link href="#" onClick={() => changeView(ChatDrawerView.AskFinn)} sx={currentView === ChatDrawerView.AskFinn ? styles.viewLinkActive : styles.viewLinkInactive}>
                Ask Finn
              </Link>
            </Box>
            <Box sx={currentView === ChatDrawerView.FinnTips ? styles.viewActive : styles.viewInactive}>
              <Link href="#" onClick={() => changeView(ChatDrawerView.FinnTips)} sx={currentView === ChatDrawerView.FinnTips ? styles.viewLinkActive : styles.viewLinkInactive}>
                Finn Tips
              </Link>
            </Box>
          </Box>
          {/*<IconButton size="small" onClick={toggleView} sx={styles.chatCloseBtn}>*/}
          {/*  {view === ChatDrawerView.AskFinn ?  <TipsIcon/> : <img src={ChatGPTIcon} alt="chatGPT"/> }*/}
          {/*</IconButton>*/}
          <IconButton size="small" onClick={toggleDrawer} sx={styles.chatCloseBtn}>
            {isOpen ?  <ChevronRightIcon/> : <ChevronLeftIcon/> }
          </IconButton>
        </Box>

        {/*<IconButton onClick={handleDrawerClose}>*/}
        {/*  {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}*/}
        {/*</IconButton>*/}
        {currentView === ChatDrawerView.AskFinn &&
          <iframe
            src="https://www.chatbase.co/chatbot-iframe/UwMm3JBWLZX_M4yxT1dgm"
            width="100%"
            style={{height: "calc(100vh - 78px)", minHeight: 600, width: openWidth}}
            frameBorder="0"
          ></iframe>
        }
        {currentView === ChatDrawerView.FinnTips && renderTips()
        }
      </Box>
    </Drawer>
  )
}

export default ChatbotDrawer